import React, { useState } from "react";
import { deleteStrategy, enterStrategy, exitStrategy, resetStrategy } from "../api";
import PnlChart from "./PnlChart";

const STATUS_LABEL = {
  idle: "Idle",
  entered: "Entered",
  exited: "Exited",
  entry_failed: "Entry failed",
  exit_failed: "Exit incomplete",
};

const REASON_LABEL = {
  target: "target hit",
  stoploss: "stop-loss hit",
  session_end: "session end",
  square_off_time: "scheduled square-off time",
  manual: "manual",
};

function formatTime12h(value24) {
  if (!value24) return "";
  const [hStr, mStr] = value24.split(":");
  const h = parseInt(hStr, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  let h12 = h % 12;
  if (h12 === 0) h12 = 12;
  return `${h12}:${mStr} ${ampm}`;
}

const WEEKDAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const WEEKDAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

function formatEntryDays(days) {
  if (!days || days.length === 0) return "no days selected";
  if (days.length === 5 && ["mon", "tue", "wed", "thu", "fri"].every((d) => days.includes(d))) return "weekdays";
  if (days.length === 7) return "every day";
  return WEEKDAY_ORDER.filter((d) => days.includes(d)).map((d) => WEEKDAY_LABEL[d]).join(", ");
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function combinedPnl(strategy, positions) {
  const bySymbol = new Map(positions.map((p) => [p.tradingsymbol, p.pnl ?? 0]));
  return strategy.symbols.reduce((sum, sym) => sum + (bySymbol.get(sym) ?? 0), 0);
}

function StrategyCard({ strategy, positions, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [showChart, setShowChart] = useState(false);
  // One-off lot override for the next Enter Now click - starts at the
  // strategy's saved default but is never written back to it; typing a
  // different number here only sizes *this* entry, same as before.
  const [lotsInput, setLotsInput] = useState(strategy.lots);

  const pnl = combinedPnl(strategy, positions);

  const run = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      if (onRefresh) onRefresh();
    } catch (err) {
      setMessage(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="strategy-card">
      <div className="strategy-card-header">
        <div>
          <strong>{strategy.name}</strong>
          <span className="muted">
            {" — "}
            {strategy.underlying === "MANUAL"
              ? "manually tracked legs"
              : strategy.expiry_rule === "nearest_weekly"
              ? `${strategy.underlying} · Auto (nearest weekly)`
              : `${strategy.underlying} ${strategy.expiry}`}
          </span>
        </div>
        <span
          className={`status-pill ${
            strategy.status === "entered"
              ? "ok"
              : strategy.status === "entry_failed" || strategy.status === "exit_failed"
              ? "danger"
              : "warn"
          }`}
        >
          {STATUS_LABEL[strategy.status] || strategy.status}
        </span>
      </div>

      {(strategy.expiry_rule === "nearest_weekly" && strategy.last_entered_expiry) || strategy.last_entered_lots ? (
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          {strategy.expiry_rule === "nearest_weekly" && strategy.last_entered_expiry
            ? `Last entered against ${strategy.last_entered_expiry} expiry`
            : ""}
          {strategy.expiry_rule === "nearest_weekly" && strategy.last_entered_expiry && strategy.last_entered_lots
            ? " · "
            : ""}
          {strategy.last_entered_lots ? `${strategy.last_entered_lots} lot${strategy.last_entered_lots === 1 ? "" : "s"} used` : ""}
        </p>
      ) : null}

      {strategy.status === "idle" && strategy.legs && strategy.legs.length > 0 && (
        <ul className="symbol-list">
          {strategy.legs.map((l, i) => (
            <li key={i}>
              {l.transaction_type} {l.option_type} @ ₹{l.premium_min}{l.premium_max != null ? `-₹${l.premium_max}` : "+"}
            </li>
          ))}
        </ul>
      )}

      {strategy.symbols.length > 0 && (
        <ul className="symbol-list">
          {strategy.symbols.map((s) => <li key={s}>{s}</li>)}
        </ul>
      )}

      {strategy.status === "idle" && strategy.entry_mode !== "manual" && (
        <p className="muted schedule-line">
          Scheduled {formatEntryDays(strategy.entry_days)}, {strategy.entry_window_start}
          {strategy.entry_window_end ? `-${strategy.entry_window_end}` : ""}
        </p>
      )}

      {strategy.status === "idle" && strategy.square_off_time && (
        <p className="muted schedule-line">
          Auto square-off at {formatTime12h(strategy.square_off_time)} if target/SL not hit
        </p>
      )}

      {strategy.status === "entered" && (
        <div className={`combined-pnl ${pnl >= 0 ? "pnl-positive" : "pnl-negative"}`}>
          {formatNumber(pnl)}
          <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
            {" "}(target {strategy.resolved_target != null ? formatNumber(strategy.resolved_target) : "none"}
            {" / SL "}{strategy.resolved_stoploss != null ? formatNumber(strategy.resolved_stoploss) : "none"})
          </span>
          {strategy.stoploss_mode !== "fixed" && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {strategy.stoploss_mode === "trailing" ? "Trailing SL active" : "Profit lock active"} once profit crosses{" "}
              {formatNumber(strategy.stoploss_mode === "trailing" ? strategy.trail_trigger : strategy.lock_trigger)}
              {strategy.move_sl_to_cost ? ` · SL moves to breakeven after ${formatNumber(strategy.move_sl_to_cost_trigger)}` : ""}
            </div>
          )}
          {strategy.square_off_time && (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Auto square-off at {formatTime12h(strategy.square_off_time)} if target/SL not hit by then
            </div>
          )}
        </div>
      )}

      {strategy.notes && <p className="banner error">{strategy.notes}</p>}
      {strategy.exit_reason && (
        <p className="muted">
          {strategy.status === "exit_failed" ? "Exit triggered" : "Exited"}:{" "}
          {REASON_LABEL[strategy.exit_reason] || strategy.exit_reason} at {strategy.exit_time}
        </p>
      )}

      <div className="button-row">
        {strategy.status === "idle" && (
          <>
            <input
              type="number"
              min="1"
              value={lotsInput}
              onChange={(e) => setLotsInput(e.target.value)}
              disabled={busy}
              title="Lots for this entry only - doesn't change the saved strategy"
              style={{ width: 64 }}
            />
            <button
              onClick={() => {
                const n = Number(lotsInput);
                if (!n || n < 1) {
                  setMessage("Lots must be a positive number.");
                  return;
                }
                run(() => enterStrategy(strategy.id, n));
              }}
              disabled={busy}
            >
              Enter Now
            </button>
            <button className="danger" onClick={() => run(() => deleteStrategy(strategy.id))} disabled={busy}>
              Delete
            </button>
          </>
        )}

        {strategy.status === "entered" && (
          <button
            className="danger"
            onClick={() => run(() => exitStrategy(strategy.id), `Exit "${strategy.name}" now with market orders?`)}
            disabled={busy}
          >
            Exit All Now
          </button>
        )}

        {strategy.status === "entry_failed" && strategy.symbols.length > 0 && (
          <button
            className="danger"
            onClick={() => run(() => exitStrategy(strategy.id), "Flatten the partially-filled legs now?")}
            disabled={busy}
          >
            Exit Partial Fill
          </button>
        )}

        {strategy.status === "exit_failed" && (
          <button
            className="danger"
            onClick={() => run(() => exitStrategy(strategy.id), "Retry flattening the still-open leg(s) now?")}
            disabled={busy}
          >
            Retry Exit
          </button>
        )}

        {(strategy.status === "exited" || (strategy.status === "entry_failed" && strategy.symbols.length === 0)) && (
          <>
            <button onClick={() => run(() => resetStrategy(strategy.id))} disabled={busy}>
              Reset for Reuse
            </button>
            <button className="danger" onClick={() => run(() => deleteStrategy(strategy.id))} disabled={busy}>
              Delete
            </button>
          </>
        )}

        {(strategy.status === "entered" || strategy.status === "exited") && (
          <button onClick={() => setShowChart((v) => !v)} disabled={busy}>
            {showChart ? "Hide" : "Show"} P&amp;L chart
          </button>
        )}
      </div>

      {message && <div className="banner error">{message}</div>}
      {showChart && <PnlChart strategyId={strategy.id} />}
    </div>
  );
}

export default function StrategyList({ strategies, positions, onRefresh }) {
  if (!strategies || strategies.length === 0) {
    return <p className="muted">No strategies yet. Build one above, or track manually-opened legs from the Positions tab.</p>;
  }

  return (
    <div className="strategy-list">
      {strategies.map((s) => (
        <StrategyCard key={s.id} strategy={s} positions={positions} onRefresh={onRefresh} />
      ))}
    </div>
  );
}
